/* HeyBear · scalable store locator (Leaflet + CSV/JSON store feed) */
(() => {
  const SELECTOR = '[data-hb-store-locator]';
  const EARTH_KM = 6371;

  const asNumber = (value) => {
    const number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : null;
  };

  const clean = (value) => String(value ?? '').trim();

  const normaliseStore = (store, index) => {
    const latitude = asNumber(store.latitude ?? store.lat);
    const longitude = asNumber(store.longitude ?? store.lng ?? store.lon);
    return {
      id: clean(store.id) || `store-${index}`,
      name: clean(store.name || store.store_name || store.title) || '7-Eleven',
      retailer: clean(store.retailer || store.brand) || '7-Eleven',
      address: clean(store.address || store.street || store.street_address),
      suburb: clean(store.suburb || store.city || store.locality),
      state: clean(store.state || store.region),
      postcode: clean(store.postcode || store.postal_code || store.zip),
      phone: clean(store.phone || store.telephone),
      latitude,
      longitude,
      url: clean(store.url || store.link || store.directions_url),
      products: clean(store.products || store.stock || store.availability),
      distance: null,
    };
  };

  const parseCsv = (text) => {
    text = text.replace(/^\uFEFF/, '');
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;
    for (let i = 0; i < text.length; i += 1) {
      const char = text[i];
      if (quoted) {
        if (char === '"' && text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else if (char === '"') {
          quoted = false;
        } else {
          field += char;
        }
      } else if (char === '"') {
        quoted = true;
      } else if (char === ',') {
        row.push(field);
        field = '';
      } else if (char === '\n') {
        row.push(field);
        rows.push(row);
        row = [];
        field = '';
      } else if (char !== '\r') {
        field += char;
      }
    }
    if (field || row.length) {
      row.push(field);
      rows.push(row);
    }
    const headers = (rows.shift() || []).map((header) => clean(header).replace(/^\uFEFF/, '').toLowerCase().replace(/[\s-]+/g, '_'));
    return rows
      .filter((values) => values.some((value) => clean(value)))
      .map((values) => Object.fromEntries(headers.map((header, index) => [header, values[index] ?? ''])));
  };

  const distanceKm = (from, store) => {
    if (store.latitude === null || store.longitude === null) return null;
    const radians = (degrees) => (degrees * Math.PI) / 180;
    const dLat = radians(store.latitude - from.latitude);
    const dLng = radians(store.longitude - from.longitude);
    const lat1 = radians(from.latitude);
    const lat2 = radians(store.latitude);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return EARTH_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const addressLine = (store) => [store.address, store.suburb, store.state, store.postcode].filter(Boolean).join(', ');

  class StoreLocator {
    constructor(root) {
      this.root = root;
      this.search = root.querySelector('[data-locator-search]');
      this.nearby = root.querySelector('[data-locator-nearby]');
      this.status = root.querySelector('[data-locator-status]');
      this.list = root.querySelector('[data-locator-list]');
      this.more = root.querySelector('[data-locator-more]');
      this.empty = root.querySelector('[data-locator-empty]');
      this.emptyHeading = root.querySelector('[data-locator-empty-heading]');
      this.emptyText = root.querySelector('[data-locator-empty-text]');
      this.results = root.querySelector('.hb-locator__results');
      this.mapElement = root.querySelector('[data-locator-map]');
      this.batchSize = Math.max(10, Number.parseInt(root.dataset.batchSize || '30', 10));
      this.retailerLogo = clean(root.dataset.retailerLogo);
      this.visibleCount = this.batchSize;
      this.stores = [];
      this.filtered = [];
      this.inView = null;
      this.markers = new Map();
      this.map = null;
      this.mapSyncFrame = 0;
      this.userLocation = null;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.abortController = new AbortController();
      this.initialise();
    }

    async initialise() {
      this.bindEvents();
      try {
        const manual = this.readManualStores();
        const remote = await this.readRemoteStores();
        this.stores = [...remote, ...manual]
          .map(normaliseStore)
          .filter((store) => store.name || store.address || store.suburb);
        this.filtered = [...this.stores];
        this.render();
        this.initialiseMap();
        requestAnimationFrame(() => this.fitInitialList());
      } catch (error) {
        console.error('[HeyBear store locator]', error);
        this.status.textContent = 'We could not load the store list. Please try again soon.';
        this.empty.hidden = false;
      }
    }

    bindEvents() {
      const { signal } = this.abortController;
      this.search.addEventListener('input', () => this.filter(), { signal });
      this.nearby.addEventListener('click', () => this.locate(), { signal });
      this.more.addEventListener('click', () => {
        this.visibleCount += this.batchSize;
        this.renderList();
      }, { signal });
    }

    readManualStores() {
      const script = this.root.querySelector('[data-locator-manual-stores]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent || '[]');
      } catch (error) {
        console.warn('[HeyBear store locator] Invalid manual store data', error);
        return [];
      }
    }

    async readRemoteStores() {
      const url = clean(this.root.dataset.dataUrl);
      if (!url) return [];
      const response = await fetch(url, { credentials: 'omit', mode: 'cors' });
      if (!response.ok) throw new Error(`Store data returned ${response.status}`);
      const text = await response.text();
      const trimmed = text.trim();
      if (!trimmed) return [];
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        const json = JSON.parse(trimmed);
        return Array.isArray(json) ? json : (json.stores || json.locations || []);
      }
      return parseCsv(text);
    }

    initialiseMap() {
      if (!window.L || !this.mapElement) return;
      const defaultLat = asNumber(this.root.dataset.defaultLat) ?? -25.2744;
      const defaultLng = asNumber(this.root.dataset.defaultLng) ?? 133.7751;
      const defaultZoom = Number.parseInt(this.root.dataset.defaultZoom || '4', 10);
      this.map = window.L.map(this.mapElement, {
        center: [defaultLat, defaultLng],
        zoom: defaultZoom,
        preferCanvas: true,
        scrollWheelZoom: false,
        touchZoom: true,
        dragging: true,
        zoomSnap: 0.25,
        zoomDelta: 0.5,
      });
      window.L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(this.map);
      this.root.dataset.mapZoom = String(this.map.getZoom());
      this.map.on('zoomend', () => { this.root.dataset.mapZoom = String(this.map.getZoom()); });
      this.map.on('moveend', () => this.scheduleMapListSync());
      this.map.on('dragstart zoomstart', () => this.map.closePopup());
      this.bindMapGestures();
      this.renderMarkers(false);
      requestAnimationFrame(() => {
        this.map.invalidateSize();
        this.renderMarkers(true);
      });
    }

    bindMapGestures() {
      let wheelDelta = 0;
      let pointer = null;
      const threshold = 18;
      this.mapElement.addEventListener('wheel', (event) => {
        if (!event.metaKey && !event.ctrlKey) return;
        event.preventDefault();
        event.stopPropagation();
        const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
        wheelDelta += -delta;
        pointer = this.map.mouseEventToContainerPoint(event);
        const steps = Math.trunc(Math.abs(wheelDelta) / threshold);
        if (!steps) return;
        const direction = Math.sign(wheelDelta);
        const zoomChange = direction * Math.min(1, steps * 0.25);
        wheelDelta -= direction * steps * threshold;
        const nextZoom = Math.max(
          this.map.getMinZoom(),
          Math.min(this.map.getMaxZoom(), this.map.getZoom() + zoomChange),
        );
        this.map.setZoomAround(pointer, nextZoom);
      }, { passive: false, signal: this.abortController.signal });
    }

    filter() {
      const terms = clean(this.search.value).toLowerCase().split(/\s+/).filter(Boolean);
      this.visibleCount = this.batchSize;
      this.inView = null;
      this.filtered = this.stores.filter((store) => {
        if (!terms.length) return true;
        const haystack = [store.name, store.retailer, store.address, store.suburb, store.state, store.postcode, store.products]
          .join(' ')
          .toLowerCase();
        return terms.every((term) => haystack.includes(term));
      });
      if (this.userLocation) this.sortByDistance();
      this.render();
      this.renderMarkers(Boolean(terms.length));
      if (!terms.length) this.scheduleMapListSync();
    }

    locate() {
      if (!navigator.geolocation) {
        this.status.textContent = 'Location is not available in this browser. Search by suburb or postcode instead.';
        return;
      }
      this.nearby.disabled = true;
      this.status.textContent = 'Finding stores near you…';
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.userLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          this.stores.forEach((store) => { store.distance = distanceKm(this.userLocation, store); });
          this.filtered = [...this.stores];
          this.inView = null;
          this.sortByDistance();
          this.visibleCount = this.batchSize;
          this.nearby.disabled = false;
          this.render();
          this.renderMarkers(false);
          if (this.map) this.map.setView([this.userLocation.latitude, this.userLocation.longitude], 11);
        },
        () => {
          this.nearby.disabled = false;
          this.status.textContent = 'We could not use your location. Search by suburb or postcode instead.';
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
      );
    }

    sortByDistance() {
      this.filtered.sort((a, b) => (a.distance ?? Number.POSITIVE_INFINITY) - (b.distance ?? Number.POSITIVE_INFINITY));
    }

    render() {
      const total = this.filtered.length;
      const query = clean(this.search.value);
      if (total === 0 && !query) {
        this.status.textContent = 'Search by suburb or postcode, or use your location.';
      } else if (total === 0) {
        this.status.textContent = `No participating stores match “${query}”.`;
      } else {
        this.status.textContent = total === 1 ? '1 participating store found.' : `${total} participating stores found.`;
      }
      this.empty.hidden = total !== 0;
      this.results.classList.toggle('is-empty', total === 0);
      if (total === 0) {
        this.emptyHeading.textContent = query ? this.empty.dataset.emptyHeading : this.empty.dataset.initialHeading;
        this.emptyText.textContent = query ? this.empty.dataset.emptyText : this.empty.dataset.initialText;
      }
      this.renderList();
    }

    scheduleMapListSync() {
      if (this.mapSyncFrame) cancelAnimationFrame(this.mapSyncFrame);
      this.mapSyncFrame = requestAnimationFrame(() => {
        this.mapSyncFrame = 0;
        this.syncListToMap();
      });
    }

    syncListToMap() {
      if (!this.map) return;
      const bounds = this.map.getBounds().pad(0.015);
      const center = this.map.getCenter();
      this.inView = this.filtered
        .filter((store) => {
          if (store.latitude === null || store.longitude === null) return false;
          return bounds.contains([store.latitude, store.longitude]);
        })
        .sort((a, b) => {
          const distanceA = center.distanceTo([a.latitude, a.longitude]);
          const distanceB = center.distanceTo([b.latitude, b.longitude]);
          return distanceA - distanceB;
        });
      this.visibleCount = this.batchSize;
      const count = this.inView.length;
      const query = clean(this.search.value);
      if (count === 0) {
        this.status.textContent = query ? '0 matching stores in this map view.' : '0 stores in this map view.';
      } else if (query && count < this.filtered.length) {
        this.status.textContent = `${count} of ${this.filtered.length} matching stores in this map view.`;
      } else {
        this.status.textContent = count === 1 ? '1 store in this map view.' : `${count} stores in this map view.`;
      }
      this.renderList();
      requestAnimationFrame(() => this.fitInitialList());
    }

    fitInitialList() {
      const firstCard = this.list.querySelector('.hb-store-card');
      if (!firstCard || this.more.hidden) return;
      const listStyles = getComputedStyle(this.list);
      const moreStyles = getComputedStyle(this.more);
      const gap = Number.parseFloat(listStyles.rowGap || listStyles.gap) || 0;
      const listPadding = (Number.parseFloat(listStyles.paddingTop) || 0) + (Number.parseFloat(listStyles.paddingBottom) || 0);
      const moreSpace = this.more.getBoundingClientRect().height
        + (Number.parseFloat(moreStyles.marginTop) || 0)
        + (Number.parseFloat(moreStyles.marginBottom) || 0);
      const available = this.results.clientHeight - moreSpace - listPadding;
      const cardHeight = firstCard.getBoundingClientRect().height;
      const fittingCount = Math.max(1, Math.ceil((available + gap) / (cardHeight + gap)));
      if (fittingCount < this.visibleCount) {
        this.visibleCount = fittingCount;
        this.renderList();
      }
    }

    renderList() {
      const stores = this.inView ?? this.filtered;
      this.list.replaceChildren();
      if (stores.length === 0) {
        const viewEmpty = document.createElement('div');
        viewEmpty.className = 'hb-locator__view-empty';
        viewEmpty.setAttribute('role', 'status');
        const title = document.createElement('strong');
        title.textContent = 'No stores in this view.';
        const text = document.createElement('p');
        text.textContent = 'Move the map or zoom out to see nearby stores.';
        viewEmpty.append(title, text);
        this.list.append(viewEmpty);
        this.more.hidden = true;
        return;
      }
      stores.slice(0, this.visibleCount).forEach((store, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'hb-store-card';
        button.dataset.storeId = store.id;
        button.setAttribute('aria-label', `Show ${store.name}, ${addressLine(store)} on the map`);

        const badge = document.createElement('span');
        badge.className = 'hb-store-card__badge';
        badge.setAttribute('aria-hidden', 'true');
        if (store.retailer.toLowerCase().includes('7') && this.retailerLogo) {
          const retailerLogo = document.createElement('img');
          retailerLogo.src = this.retailerLogo;
          retailerLogo.alt = '';
          retailerLogo.loading = 'lazy';
          retailerLogo.decoding = 'async';
          badge.append(retailerLogo);
        } else {
          badge.textContent = store.retailer.slice(0, 1).toUpperCase();
        }

        const content = document.createElement('div');
        content.className = 'hb-store-card__content';
        const top = document.createElement('div');
        top.className = 'hb-store-card__top';
        const name = document.createElement('strong');
        name.textContent = store.name;
        top.append(name);
        if (store.distance !== null) {
          const distance = document.createElement('span');
          distance.className = 'hb-store-card__distance';
          distance.textContent = store.distance < 10 ? `${store.distance.toFixed(1)} km` : `${Math.round(store.distance)} km`;
          top.append(distance);
        }
        content.append(top);

        const address = document.createElement('address');
        address.textContent = addressLine(store) || 'Address coming soon';
        content.append(address);

        const meta = document.createElement('div');
        meta.className = 'hb-store-card__meta';
        if (store.phone) {
          const phone = document.createElement('span');
          phone.textContent = store.phone;
          meta.append(phone);
        }
        if (store.products) {
          const products = document.createElement('span');
          products.textContent = store.products;
          meta.append(products);
        }
        content.append(meta);
        button.append(badge, content);
        button.addEventListener('click', () => this.selectStore(store, button));
        this.list.append(button);
        if (index === 0 && stores.length === 1) button.classList.add('is-selected');
      });
      this.more.hidden = this.visibleCount >= stores.length;
      this.list.append(this.more);
    }

    renderMarkers(fitBounds) {
      if (!this.map || !window.L) return;
      this.markers.forEach((marker) => marker.remove());
      this.markers.clear();
      const points = [];
      this.filtered.forEach((store) => {
        if (store.latitude === null || store.longitude === null) return;
        const marker = window.L.circleMarker([store.latitude, store.longitude], {
          radius: 8,
          color: '#0f1c2e',
          weight: 3,
          fillColor: '#e1443a',
          fillOpacity: 1,
        }).addTo(this.map);
        marker.bindPopup(this.popupContent(store), {
          autoPan: true,
          autoPanPaddingTopLeft: [24, 76],
          autoPanPaddingBottomRight: [24, 24],
          keepInView: true,
          maxWidth: 290,
        });
        marker.on('click', () => this.highlightCard(store.id));
        this.markers.set(store.id, marker);
        points.push([store.latitude, store.longitude]);
      });
      if (fitBounds && points.length) {
        this.map.fitBounds(points, { padding: window.innerWidth <= 800 ? [30, 30] : [56, 56], maxZoom: 13, animate: false });
      }
    }

    popupContent(store) {
      const node = document.createElement('div');
      const title = document.createElement('strong');
      title.textContent = store.name;
      const address = document.createElement('div');
      address.textContent = addressLine(store);
      node.append(title, address);
      if (store.phone) {
        const phone = document.createElement('a');
        phone.className = 'hb-locator-popup__phone';
        phone.href = `tel:${store.phone.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '')}`;
        phone.textContent = store.phone;
        node.append(phone);
      }
      const actions = document.createElement('div');
      actions.className = 'hb-locator-popup__actions';
      const directions = document.createElement('a');
      const destination = store.latitude !== null && store.longitude !== null
        ? `${store.latitude},${store.longitude}`
        : addressLine(store);
      directions.className = 'hb-locator-popup__directions';
      directions.href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
      directions.target = '_blank';
      directions.rel = 'noopener noreferrer';
      directions.textContent = 'Get directions';
      actions.append(directions);
      if (store.url) {
        const link = document.createElement('a');
        link.className = 'hb-locator-popup__details';
        link.href = store.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = 'Store details';
        actions.append(link);
      }
      node.append(actions);
      return node;
    }

    selectStore(store, button) {
      this.highlightCard(store.id, button);
      const marker = this.markers.get(store.id);
      if (marker && this.map) {
        const target = marker.getLatLng();
        const targetZoom = Math.max(this.map.getZoom(), 13);
        const openPopup = () => {
          marker.openPopup();
          this.map.panInside(target, {
            paddingTopLeft: [48, 92],
            paddingBottomRight: [48, 48],
            animate: !this.reduceMotion,
          });
        };
        if (this.map.getCenter().equals(target, 0.00001) && this.map.getZoom() === targetZoom) {
          openPopup();
        } else {
          this.map.once('moveend', openPopup);
          this.map.setView(target, targetZoom, { animate: !this.reduceMotion });
        }
      }
    }

    highlightCard(id, knownButton) {
      this.list.querySelectorAll('.hb-store-card').forEach((card) => {
        card.classList.toggle('is-selected', card.dataset.storeId === id);
      });
      const button = knownButton || this.list.querySelector(`[data-store-id="${CSS.escape(id)}"]`);
      if (button && !knownButton) {
        const targetTop = Math.max(0, button.offsetTop - this.list.offsetTop - 12);
        this.list.scrollTo({ top: targetTop, behavior: this.reduceMotion ? 'auto' : 'smooth' });
      }
    }
  }

  const initialiseAll = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.locatorReady === 'true') return;
      root.dataset.locatorReady = 'true';
      new StoreLocator(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true });
  } else {
    initialiseAll();
  }
  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
